/**
 * The closing panel's sections — the pieces the calendar detail drawer and the
 * drilldown Closing tab BOTH render (closing-readiness.md §3: "the full object
 * for the sidebar / the Closing tab on the loan"). One implementation, two
 * hosts; the hosts only differ in chrome (the drawer adds the identity crumb +
 * "Open loan file", the tab adds the card header + sub-tab strip).
 *
 *   StatusStrip     pipeline status · event / funding sub-status · readiness
 *                   pill · the date placing the loan on the calendar + flags
 *   Callout         what stands between this loan and a schedule
 *   FundedLine      the closed-state confirmation
 *   SectionHead     the caps landmark heading ("READINESS")
 *   PanelSection    a quiet foldable section (Closing logistics, People, Wire)
 *   ClosingLogisticsSection / PeopleContactsSection / WireInstructionsSection
 *
 * `view` is a schedule CARD shape (a real card, or `readinessToCard(readiness)`
 * lifted from the full object); `full` is the loan itself (contacts + wire
 * instructions live there, not on the readiness object).
 */
import { useCallback, useMemo, useState } from "react";

import AccountBalanceOutlined from "@mui/icons-material/AccountBalanceOutlined";
import ArrowDownwardRounded from "@mui/icons-material/ArrowDownwardRounded";
import CalendarTodayOutlined from "@mui/icons-material/CalendarTodayOutlined";
import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import KeyboardArrowRightRounded from "@mui/icons-material/KeyboardArrowRightRounded";
import MailOutlineRounded from "@mui/icons-material/MailOutlineRounded";
import PhoneOutlined from "@mui/icons-material/PhoneOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import WorkOutlineOutlined from "@mui/icons-material/WorkOutlineOutlined";
import { Box, IconButton, Link as MuiLink, Tooltip, alpha } from "@mui/material";
import { format } from "date-fns";
import { useSelector } from "react-redux";

import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import { amber, slate, status, steel, teal } from "../../theme/colors";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import { ChecklistRow, useCollapsible } from "./ReadinessDetail";
import { Muted, Pill } from "./ReadinessBits";
import {
  C,
  MONO,
  cardAmountLabel,
  cardAttentionChips,
  cardHasTime,
  cardLevelMeta,
  closedLabel,
  eventNoun,
  pipelineStatusLabel,
} from "./closingReadinessUtils";
import { INK, TYPE } from "./typography";

// The status strip's meta line and the callouts share one date voice.
export const shortDate = (d) => (d ? format(d, "EEE, MMM d, yyyy") : "");
export const longDate = (d) => (d ? format(d, "MMMM d, yyyy") : "");

// A scheduling field key the backend didn't label ("closingScheduled") → words.
const humanizeField = (key) =>
  String(key || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (ch) => ch.toUpperCase())
    .toLowerCase()
    .replace(/^./, (ch) => ch.toUpperCase());

const fullName = (c) => [c?.firstName, c?.lastName].filter(Boolean).join(" ").trim();

// ── Landmarks ───────────────────────────────────────────────────────────────
// The caps landmark heading ("READINESS") with its icon and an optional
// control on the right.
export const SectionHead = ({ icon: Icon, title, right }) => (
  <Flx ac g={1.125} sx={{ mb: 0.5 }}>
    {Icon && <Icon sx={{ fontSize: 16, color: C.faint }} />}
    <Txt sx={{ ...TYPE.sectionH, fontSize: 10.5 }}>{title}</Txt>
    {right != null && <Box sx={{ ml: "auto" }}>{right}</Box>}
  </Flx>
);

// A full-bleed hairline between the panel's zones. `bleed` is the host's edge
// padding (the drawer's 26px) so the line runs edge to edge; omit it for a
// host whose content already sits flush.
export const PanelDivider = ({ bleed, sx }) => (
  <Box sx={{ height: "1px", background: C.line, mx: bleed ? `-${bleed}` : 0, my: 2.75, ...sx }} />
);

// A quiet section the closer can fold: one flat row — icon · caps title ·
// badges · chevron — that toggles its body. Starts folded unless `defaultOpen`
// says otherwise. Consecutive sections are separated by a thin hairline.
export const PanelSection = ({ title, icon: Icon, badge, children, defaultOpen = false }) => {
  const [open, setOpen] = useCollapsible(defaultOpen);
  return (
    <Box sx={{ "& + &": { borderTop: `1px solid ${C.lineSoft}` } }}>
      <Flx
        ac
        g={1.25}
        onClick={() => setOpen((o) => !o)}
        sx={{
          px: "2px",
          py: 1.75,
          borderRadius: "8px",
          cursor: "pointer",
          userSelect: "none",
          transition: "background 0.12s",
          "&:hover": { background: C.lineSoft },
        }}
      >
        {Icon && <Icon sx={{ fontSize: 16, color: C.faint, flexShrink: 0 }} />}
        <Txt sx={{ ...TYPE.sectionH, flex: 1, minWidth: 0 }}>{title}</Txt>
        {badge != null && (
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "7px",
              flexShrink: 0,
              fontFamily: MONO,
              "& *": { fontFamily: MONO },
            }}
          >
            {badge}
          </Box>
        )}
        <KeyboardArrowRightRounded
          sx={{
            fontSize: 16,
            color: C.faint,
            transition: "transform 0.18s",
            transform: open ? "rotate(90deg)" : "none",
            flexShrink: 0,
          }}
        />
      </Flx>
      {open && <Box sx={{ px: "2px", pt: 0.5, pb: 2.5 }}>{children}</Box>}
    </Box>
  );
};

// ── Status strip ────────────────────────────────────────────────────────────
// One status strip, stated once: the signal dot, the pipeline stage, the
// event · funding sub-status, the readiness pill; then a meta line with the
// date that places this loan on the calendar and the attention flags.
// `pending` = the readiness signal hasn't arrived yet (opened from the loan,
// where the panel is seeded from the loan rather than a schedule card). The
// level is a backend verdict, so say "Checking" rather than let the fallback
// print "Not scheduled" at a loan that may well be ready.
export const StatusStrip = ({ view, trade, date, closed, pending, sx }) => {
  const m = cardLevelMeta(view);
  const green = status.success;
  // The dot is the LEVEL colour (same as the pill), not the Capital Markets
  // signal: Not scheduled reads magenta everywhere, never the Blocked red.
  const dot = closed ? green.solid : pending ? slate[400] : m.c;
  const pill = closed
    ? { label: closedLabel(view), c: green.fg, bg: green.bg, dot: green.solid }
    : pending
      ? { label: "Checking…", c: slate[600], bg: slate[100], dot }
      : { label: m.label, c: m.c, bg: m.bg, dot };
  const sub = closed
    ? trade
      ? "Settled"
      : "Funded"
    : pending
      ? eventNoun(view)
      : `${eventNoun(view)} · ${view?.fundingReady ? "ready to fund" : "not ready to fund"}`;
  const dateLabel = closed
    ? closedLabel(view)
    : view?.dateBasis === "scheduled"
      ? "Scheduled close"
      : view?.dateBasis === "settlement"
        ? "Settlement"
        : view?.dateBasis === "expected"
          ? "Expected close"
          : null;
  const d = view?.daysToFunding;
  const relative =
    closed || d == null ? null : d === 0 ? "TODAY" : d < 0 ? `${-d}D PAST` : `IN ${d}D`;
  // The "Closed" flag repeats the pill — the rest of the flags stay.
  const chips = cardAttentionChips(view).filter((ch) => ch.key !== "closed");

  return (
    <Box sx={{ mt: 2, border: `1px solid ${C.line}`, borderRadius: "14px", overflow: "hidden", ...sx }}>
      <Flx ac wrap g={1.125} sx={{ px: 2, pt: "13px", pb: "12px" }}>
        <Box sx={{ width: 9, height: 9, borderRadius: "50%", background: dot, flexShrink: 0 }} />
        <Txt sx={{ ...TYPE.status, fontSize: 13 }}>{pipelineStatusLabel(view)}</Txt>
        <Txt sx={TYPE.sub}>{sub}</Txt>
        <Flx
          ac
          g={0.6}
          sx={{
            ml: "auto",
            px: 1.125,
            py: "3px",
            borderRadius: "99px",
            background: pill.bg,
            color: pill.c,
            flexShrink: 0,
          }}
        >
          <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: pill.dot }} />
          <Txt sx={{ ...TYPE.micro, fontWeight: 600, color: pill.c, whiteSpace: "nowrap" }}>{pill.label}</Txt>
        </Flx>
      </Flx>
      <Flx
        ac
        wrap
        g={1}
        sx={{ px: 2, pt: "10px", pb: "12px", borderTop: `1px solid ${C.lineSoft}`, background: slate[50] }}
      >
        <CalendarTodayOutlined sx={{ fontSize: 14, color: C.faint, flexShrink: 0 }} />
        <Txt sx={{ ...TYPE.sub, fontSize: 11 }}>
          {date ? (
            <>
              {dateLabel ? `${dateLabel} ` : ""}
              <Box component="span" sx={{ color: INK, fontWeight: 600 }}>
                {shortDate(date)}
                {cardHasTime(view) ? ` · ${formatDateTime(view.fundingDate, "time")}` : ""}
              </Box>
            </>
          ) : (
            "No date set"
          )}
        </Txt>
        {relative && (
          <Txt
            sx={{
              ...TYPE.micro,
              fontWeight: 700,
              color: d < 0 ? status.error.fg : teal[700],
              background: d < 0 ? status.error.bg : teal[100],
              borderRadius: "5px",
              px: "7px",
              py: "2px",
            }}
          >
            {relative}
          </Txt>
        )}
        {chips.length > 0 && (
          <Flx ac wrap g={0.5} sx={{ ml: "auto" }}>
            {chips.map((ch) => (
              <Pill key={ch.key} label={ch.label} c={ch.c} bg={ch.bg} size="xs" />
            ))}
          </Flx>
        )}
      </Flx>
    </Box>
  );
};

// The secondary header actions: square icon buttons with a tooltip.
// With a `label` the button sizes to its text (icon + words, no stretch);
// without one it is a 42px square.
export const IconAction = ({ title, label, icon: Icon, onClick, disabled }) => (
  <Tooltip title={title} arrow>
    <span>
      <IconButton
        onClick={onClick}
        disabled={disabled}
        aria-label={title}
        sx={{
          width: label ? "auto" : 42,
          height: 42,
          px: label ? 1.75 : 0,
          gap: "7px",
          borderRadius: "10px",
          border: `1px solid ${C.line}`,
          background: "#fff",
          color: C.sub,
          whiteSpace: "nowrap",
          flexShrink: 0,
          transition: "border-color .14s, color .14s, background .14s",
          "&:hover": { borderColor: slate[300], color: C.ink, background: slate[50] },
        }}
      >
        <Icon sx={{ fontSize: 17 }} />
        {label && (
          <Txt sx={{ ...TYPE.bodyStrong, fontSize: 13, color: "inherit", lineHeight: 1 }}>{label}</Txt>
        )}
      </IconButton>
    </span>
  </Tooltip>
);

// ── Callouts ────────────────────────────────────────────────────────────────
// The one actionable callout under the buttons. Missing scheduling entries get
// the headline + chips + a jump link; any other reasons the level carries are
// listed beneath in plain lines. Tone follows the readiness level.
export const Callout = ({ view, readiness, onJumpToScheduling }) => {
  const level = cardLevelMeta(view).key;
  const missing = Array.isArray(view?.missingRequired) ? view.missingRequired : [];
  const fields = Array.isArray(readiness?.scheduling?.fields) ? readiness.scheduling.fields : [];
  const labelOf = (key) => fields.find((f) => f.field === key)?.label || humanizeField(key);
  // "Missing scheduling entries: a, b" is the chip row — don't say it twice.
  const reasons = (Array.isArray(view?.reasons) ? view.reasons : []).filter(
    (r) => !/^missing scheduling entries/i.test(String(r || "")),
  );
  if (!missing.length && !reasons.length) return null;

  const bad = level === "blocked" || level === "not_scheduled" || missing.length > 0;
  const warn = !bad && (level === "at_risk" || level === "ready_with_items");
  const tone = bad
    ? { fg: status.error.fg, line: alpha(status.error.solid, 0.28), bg: status.error.bg, ink: status.error.fg, Icon: WarningAmberOutlined }
    : warn
      ? { fg: amber.chipText, line: alpha(amber.main, 0.4), bg: status.warning.bg, ink: amber.chipText, Icon: WarningAmberOutlined }
      : { fg: status.success.fg, line: alpha(status.success.solid, 0.3), bg: status.success.bg, ink: status.success.fg, Icon: CheckCircleOutlineRounded };

  const headline = missing.length
    ? `${missing.length} scheduling ${missing.length === 1 ? "entry" : "entries"} needed before this loan can be scheduled`
    : reasons[0];
  const rest = missing.length ? reasons : reasons.slice(1);

  return (
    <Box sx={{ mt: 1.75, border: `1px solid ${tone.line}`, background: tone.bg, borderRadius: "10px", px: 1.75, py: 1.5 }}>
      <Flx g={1.125} sx={{ alignItems: "flex-start" }}>
        <tone.Icon sx={{ fontSize: 17, color: tone.fg, flexShrink: 0, mt: "1px" }} />
        <Txt sx={{ ...TYPE.body, fontSize: 13, fontWeight: 600, color: tone.ink, lineHeight: 1.35 }}>{headline}</Txt>
      </Flx>
      {missing.length > 0 && (
        <Flx wrap g={0.75} sx={{ mt: 1.125, ml: "26px" }}>
          {missing.map((key) => (
            <Txt
              key={key}
              sx={{
                ...TYPE.micro,
                fontWeight: 500,
                color: tone.fg,
                background: "#fff",
                border: `1px solid ${tone.line}`,
                borderRadius: "6px",
                px: "9px",
                py: "3px",
              }}
            >
              {labelOf(key)}
            </Txt>
          ))}
        </Flx>
      )}
      {rest.length > 0 && (
        <Flx column g={0.5} sx={{ mt: 1.125, ml: "26px" }}>
          {rest.map((r, i) => (
            <Flx key={i} g={0.75} sx={{ alignItems: "flex-start" }}>
              <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: tone.fg, mt: "7px", flexShrink: 0 }} />
              <Txt sx={{ ...TYPE.detail, color: tone.ink }}>{r}</Txt>
            </Flx>
          ))}
        </Flx>
      )}
      {missing.length > 0 && onJumpToScheduling && (
        <Txt
          onClick={onJumpToScheduling}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            mt: 1.25,
            ml: "26px",
            ...TYPE.toggle,
            fontSize: 12,
            color: tone.fg,
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          <ArrowDownwardRounded sx={{ fontSize: 13 }} /> Go to scheduling entries
        </Txt>
      )}
    </Box>
  );
};

// The closed-state confirmation line.
export const FundedLine = ({ trade, date }) => (
  <Flx
    ac
    g={1.125}
    sx={{
      mt: 1.75,
      px: 1.75,
      py: "11px",
      borderRadius: "10px",
      background: status.success.bg,
      border: `1px solid ${alpha(status.success.solid, 0.3)}`,
    }}
  >
    <CheckCircleOutlineRounded sx={{ fontSize: 17, color: status.success.solid, flexShrink: 0 }} />
    <Txt sx={{ ...TYPE.sub, fontSize: 13 }}>
      <Box component="span" sx={{ color: status.success.fg, fontWeight: 600 }}>
        {trade ? "Settled" : "Funded"}
        {date ? ` ${longDate(date)}` : ""}.
      </Box>{" "}
      No further action needed.
    </Txt>
  </Flx>
);

// ── Ledger (Closing logistics) ──────────────────────────────────────────────
// Two-column ledger for the quiet sections: a caps group label, then rows of
// muted label (fixed column) · value in the row voice, each row underlined
// edge to edge.
const LEDGER_LABEL_W = 180;
export const LedgerGroup = ({ label, children }) => (
  <Box sx={{ "& + &": { mt: 2.25 } }}>
    <Txt sx={{ ...TYPE.micro, letterSpacing: "0.06em", textTransform: "uppercase", color: C.faint, display: "block", mb: 0.5 }}>
      {label}
    </Txt>
    {children}
  </Box>
);

export const LedgerRow = ({ label, value, mono }) =>
  value || value === 0 ? (
    <Flx
      g={2}
      sx={{
        alignItems: "center",
        minWidth: 0,
        py: 1.1,
        borderBottom: `1px solid ${C.lineSoft}`,
      }}
    >
      <Box sx={{ width: LEDGER_LABEL_W, flexShrink: 0 }}>
        <Txt sx={{ ...TYPE.sub, color: C.faint }}>{label}</Txt>
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {typeof value === "string" || typeof value === "number" ? (
          <Txt sx={{ ...(mono ? { ...TYPE.mono, fontSize: 14, color: C.ink } : { ...TYPE.row, fontSize: 14 }), wordBreak: "break-word" }}>
            {value}
          </Txt>
        ) : (
          value
        )}
      </Box>
    </Flx>
  ) : null;

const StaffValue = ({ name, email }) => (
  <Flx ac wrap g={1} sx={{ minWidth: 0 }}>
    <Txt sx={TYPE.row}>{name}</Txt>
    <Txt sx={{ ...TYPE.mono, color: C.faint, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
      {email}
    </Txt>
  </Flx>
);

// Email → display name through the users slice (the ledger shows both).
export const useUserName = () => {
  const users = useSelector((state) => state.users);
  return useCallback(
    (email) => {
      if (!email) return "";
      const u = (Array.isArray(users) ? users : []).find(
        (x) => String(x?.emailAddress || "").toLowerCase() === String(email).toLowerCase(),
      );
      return u?.fullName || email;
    },
    [users],
  );
};

// Closing logistics — a two-column ledger in DEAL / TEAM groups: label column
// with a hairline, value column in the row voice. `onJumpToDocuments` is the
// "detail in Documents ↑" deep link into the Readiness documents block.
export const ClosingLogisticsSection = ({ view, trade, onJumpToDocuments, defaultOpen }) => {
  const userName = useUserName();
  return (
    <PanelSection
      title={trade ? "Trade logistics" : "Closing logistics"}
      icon={WorkOutlineOutlined}
      defaultOpen={defaultOpen}
    >
      <LedgerGroup label="Deal">
        <LedgerRow label="Originator" value={view?.originator} />
        <LedgerRow label="Product" value={view?.loanProductType} />
        <LedgerRow label="Loan amount" value={cardAmountLabel(view)} mono />
        {trade && view?.tradeStatus && <LedgerRow label="Trade status" value={view.tradeStatus} />}
      </LedgerGroup>
      <LedgerGroup label="Team">
        <LedgerRow
          label="Closer"
          value={view?.closer ? <StaffValue name={userName(view.closer)} email={view.closer} /> : null}
        />
        {view?.underwriter && (
          <LedgerRow
            label="Underwriter"
            value={<StaffValue name={userName(view.underwriter)} email={view.underwriter} />}
          />
        )}
        {view?.acceptedPct != null && (
          <LedgerRow
            label="Documents"
            value={
              <Flx ac wrap g={0.5}>
                <Muted sx={{ ...TYPE.sub, color: C.sub }}>
                  {Number(view.acceptedPct).toFixed(0)}% accepted
                  {onJumpToDocuments ? " · detail in" : ""}
                </Muted>
                {onJumpToDocuments && (
                  <Txt
                    onClick={onJumpToDocuments}
                    sx={{ ...TYPE.row, color: teal[700], cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                  >
                    Documents ↑
                  </Txt>
                )}
              </Flx>
            }
          />
        )}
      </LedgerGroup>
    </PanelSection>
  );
};

// ── People & contacts ───────────────────────────────────────────────────────
// One person: initials avatar · name + role line, with email / phone as icon
// chips on the right (mailto: / tel:).
const initialsOf = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "•";

const ContactChip = ({ href, icon: Icon, title }) => (
  <Tooltip title={title} arrow>
    <MuiLink
      href={href}
      underline="none"
      onClick={(e) => e.stopPropagation()}
      sx={{
        width: 28,
        height: 28,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "7px",
        border: `1px solid ${C.line}`,
        color: C.sub,
        flexShrink: 0,
        "&:hover": { borderColor: C.faint, color: C.ink, background: C.lineSoft },
      }}
    >
      <Icon sx={{ fontSize: 15 }} />
    </MuiLink>
  </Tooltip>
);

export const PersonRow = ({ name, sub, email, phone, accent }) => (
  <Flx ac g={1.25} sx={{ py: 1.1, borderBottom: `1px solid ${C.lineSoft}` }}>
    <Flx
      center
      sx={{
        width: 32,
        height: 32,
        borderRadius: "8px",
        background: accent ? steel.tint : C.lineSoft,
        color: accent ? steel.main : C.sub,
        fontSize: 11.5,
        fontWeight: 800,
        flexShrink: 0,
      }}
    >
      {initialsOf(name)}
    </Flx>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Txt
        sx={{
          ...TYPE.row,
          display: "block",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {name}
      </Txt>
      {sub && (
        <Txt sx={{ ...TYPE.meta, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {sub}
        </Txt>
      )}
    </Box>
    <Flx ac g={0.6}>
      {email && <ContactChip href={`mailto:${email}`} icon={MailOutlineRounded} title={email} />}
      {phone && <ContactChip href={`tel:${phone}`} icon={PhoneOutlined} title={phone} />}
    </Flx>
  </Flx>
);

// A title contact is required before wire verification, so its absence is flagged.
export const hasTitleContactOnFile = (full) =>
  (Array.isArray(full?.loanRelatedContacts) ? full.loanRelatedContacts : []).some((c) =>
    /title/i.test(String(c?.loanRole || "")),
  );

// People & contacts — one flat list: borrowers, then the loan team (closer /
// underwriter / processor, resolved to names through the users slice), then
// the related contacts (settlement, title, counsel, everyone else).
export const usePeopleList = (full, view) => {
  const users = useSelector((state) => state.users);
  return useMemo(() => {
    const byEmail = new Map(
      (Array.isArray(users) ? users : [])
        .filter((u) => u?.emailAddress)
        .map((u) => [String(u.emailAddress).toLowerCase(), u]),
    );
    const staff = (email, role) => {
      if (!email) return null;
      const u = byEmail.get(String(email).toLowerCase());
      return {
        key: `${role}-${email}`,
        name: u?.fullName || email,
        sub: [role, u?.company || u?.organization].filter(Boolean).join(" · "),
        email,
        phone: u?.phone || u?.phoneNumber,
      };
    };
    const out = [];
    (Array.isArray(full?.borrowers) ? full.borrowers : []).forEach((b, i) => {
      out.push({
        key: `b${i}`,
        name: fullName(b) || "Borrower",
        sub: i === 0 ? "Borrower" : "Co-borrower",
        email: b?.emailAddress,
        phone: b?.phone || b?.phoneNumber,
        accent: true,
      });
    });
    [staff(view?.closer, "Closer"), staff(view?.underwriter, "Underwriter"), staff(view?.processor, "Processor")]
      .filter(Boolean)
      .forEach((p) => out.push(p));
    (Array.isArray(full?.loanRelatedContacts) ? full.loanRelatedContacts : [])
      .filter(Boolean)
      .forEach((c, i) => {
        out.push({
          key: `c${i}`,
          name: fullName(c) || c?.company || c?.loanRole || "Contact",
          sub: [c?.loanRole, c?.company].filter(Boolean).join(" · "),
          email: c?.emailAddress,
          phone: c?.phone || c?.phoneNumber,
        });
      });
    return out;
  }, [full, users, view?.closer, view?.underwriter, view?.processor]);
};

export const PeopleContactsSection = ({ full, view, defaultOpen }) => {
  const people = usePeopleList(full, view);
  const hasTitleContact = hasTitleContactOnFile(full);
  return (
    <PanelSection
      title="People & contacts"
      icon={GroupsOutlined}
      defaultOpen={defaultOpen}
      badge={
        full ? (
          <>
            <Pill
              label={`${people.length} ${people.length === 1 ? "person" : "people"}`}
              c={C.sub}
              bg={C.lineSoft}
              size="xs"
            />
            {!hasTitleContact && (
              <Pill label="Title contact missing" c={amber.chipText} bg={amber.light} size="xs" />
            )}
          </>
        ) : null
      }
    >
      {!full ? (
        <Txt sx={TYPE.meta}>Loading…</Txt>
      ) : (
        <Box>
          {people.map((p) => (
            <PersonRow key={p.key} {...p} />
          ))}
          {/* The same missing-entry row the Readiness checklists use. */}
          {!hasTitleContact && (
            <Box sx={{ mt: 0.5 }}>
              <ChecklistRow
                tone="missing"
                title="Title contact not on file"
                sub="Needed before wire verification"
              />
            </Box>
          )}
        </Box>
      )}
    </PanelSection>
  );
};

// ── Wire instructions ───────────────────────────────────────────────────────
export const CopyField = ({ label, value }) => {
  const [done, setDone] = useState(false);
  if (!value) return null;
  const copy = () => {
    try {
      navigator.clipboard?.writeText(String(value));
    } catch (e) {
      /* clipboard unavailable */
    }
    setDone(true);
    setTimeout(() => setDone(false), 1200);
  };
  return (
    <Flx ac jb g={1.5} sx={{ py: 0.6 }}>
      <Txt sx={TYPE.meta}>{label}</Txt>
      <Flx ac g={0.75}>
        <Txt sx={{ ...TYPE.mono, color: C.ink }}>
          {value}
        </Txt>
        <IconButton
          size="small"
          onClick={copy}
          sx={{ p: 0.4, color: done ? status.success.solid : C.faint }}
        >
          {done ? (
            <CheckRounded sx={{ fontSize: 14 }} />
          ) : (
            <ContentCopyRounded sx={{ fontSize: 14 }} />
          )}
        </IconButton>
      </Flx>
    </Flx>
  );
};

export const WireInstructionsSection = ({ full, view, defaultOpen }) => {
  const wire = full?.wireInstructions || {};
  return (
    <PanelSection title="Wire instructions" icon={AccountBalanceOutlined} defaultOpen={defaultOpen}>
      {full && (wire?.nameOfBank || wire?.bankRoutingNumber || wire?.bankAccountNumber) ? (
        <>
          <Box
            sx={{
              borderRadius: "12px",
              px: 1.75,
              py: 0.5,
              background: C.bg,
              border: `1px solid ${C.line}`,
            }}
          >
            <CopyField label="Receiving bank" value={wire?.nameOfBank} />
            <CopyField label="Account owner" value={wire?.bankAccountOwnerName} />
            <CopyField label="ABA / routing" value={wire?.bankRoutingNumber} />
            <CopyField label="Account" value={wire?.bankAccountNumber} />
            <CopyField label="Amount" value={cardAmountLabel(view)} />
            <CopyField label="Reference" value={view?.loanNumber != null ? `#${view.loanNumber}` : null} />
          </Box>
          <Flx g={0.75} sx={{ mt: 1, alignItems: "flex-start" }}>
            <WarningAmberOutlined sx={{ fontSize: 13, color: amber.main, mt: 0.2 }} />
            <Txt sx={TYPE.meta}>
              Always verify instructions by phone with a known contact before sending funds.
            </Txt>
          </Flx>
        </>
      ) : (
        <Txt sx={TYPE.meta}>{full ? "No wire instructions on file" : "Loading…"}</Txt>
      )}
    </PanelSection>
  );
};
